/**
 * ENISA SRP "Q16" logical field schema (informative tab in the report editor).
 * Mirrors `src/services/src/compliance/srp-package.ts` — common fields 1–12 plus
 * the Vulnerability (v13–v26) or Incident (i13–i25) branch (REVIEW-FIXES C-2).
 * Obligation per stage: X mandatory · C carried · O optional · I if-available · A automated.
 */
import type { IncidentKind } from './contract';

export type ObligFlag = 'X' | 'C' | 'O' | 'I' | 'A' | '-';
export interface Q16Field {
  n: string;
  label: string;
  h24: ObligFlag;
  h72: ObligFlag;
  fin: ObligFlag;
  src: 'auto' | 'you';
  val?: string;
  hint?: string;
  mono?: boolean;
}

interface Ctx {
  kind: IncidentKind;
  cve: string;
  product: string;
  manufacturer: string;
  reporter: string;
  markets: string[]; // EU-only for field 11 (C-7)
  cvss: number | null;
  severity: string | null;
  title: string;
}

const common = (c: Ctx): Q16Field[] => [
  { n: '1', label: 'Notification type', h24: 'X', h72: 'C', fin: 'C', src: 'auto', val: c.kind === 'incident' ? 'Incident' : 'Vulnerability' },
  { n: '2', label: 'Notification level', h24: 'X', h72: 'X', fin: 'X', src: 'auto', val: 'Early warning (24h)' },
  { n: '3', label: 'Reporting time — 24h', h24: 'A', h72: 'A', fin: 'A', src: 'auto', val: '2026-07-18 08:12 UTC', mono: true },
  { n: '4', label: 'Reporting time — 72h', h24: 'A', h72: 'A', fin: 'A', src: 'auto', hint: 'stamped when the 72h notification is sent' },
  { n: '5', label: 'Reporting time — Final', h24: 'A', h72: 'A', fin: 'A', src: 'auto', hint: 'stamped at the final report' },
  { n: '6', label: 'Reporter', h24: 'A', h72: 'A', fin: 'A', src: 'auto', val: c.reporter },
  { n: '7', label: 'Manufacturer / OSS steward', h24: 'X', h72: 'C', fin: 'C', src: 'auto', val: c.manufacturer },
  { n: '8', label: 'Product', h24: 'X', h72: 'C', fin: 'C', src: 'auto', val: c.product, mono: true },
  { n: '9', label: 'Product type', h24: 'O', h72: 'C', fin: 'C', src: 'you', hint: 'Default / Important I / Important II / Critical' },
  { n: '10', label: 'Product category (CRA Annex III/IV)', h24: 'O', h72: 'C', fin: 'C', src: 'you', hint: 'only if type ≠ Default' },
  { n: '11', label: 'Member States available', h24: 'I', h72: 'C', fin: 'C', src: 'auto', val: c.markets.join(', '), hint: 'EU Member States only — non-EU markets (CH) are excluded automatically' },
  { n: '12', label: 'Title', h24: 'X', h72: 'C', fin: 'C', src: 'auto', val: c.title },
];

const vuln = (c: Ctx): Q16Field[] => [
  { n: 'v13', label: 'CVE ID', h24: 'O', h72: 'C', fin: 'C', src: 'auto', val: c.cve, mono: true },
  { n: 'v14', label: 'EUVD ID', h24: 'O', h72: 'C', fin: 'C', src: 'auto', hint: 'none assigned' },
  { n: 'v15', label: 'General information', h24: 'O', h72: 'X', fin: 'C', src: 'auto', val: 'RCE in the TCP/IP stack (from your general information)' },
  { n: 'v16', label: 'General nature of the vulnerability', h24: 'O', h72: 'X', fin: 'C', src: 'you', val: 'Out-of-bounds write (memory corruption)' },
  { n: 'v17', label: 'General nature of the exploit', h24: 'O', h72: 'X', fin: 'C', src: 'auto', val: 'Active exploitation observed (CISA KEV)' },
  { n: 'v18', label: 'Corrective measures taken', h24: 'O', h72: 'X', fin: 'C', src: 'you', hint: 'patch / mitigation you issued' },
  { n: 'v19', label: 'Measures users can take', h24: 'O', h72: 'X', fin: 'C', src: 'you', hint: 'workarounds, config, monitoring' },
  { n: 'v20', label: 'Considered sensitivity of information', h24: 'O', h72: 'I', fin: 'C', src: 'you', val: 'false' },
  { n: 'v21', label: 'Date corrective measure became available', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'anchors the 14-day final clock' },
  { n: 'v22', label: 'Full description', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'mandatory at Final' },
  { n: 'v23', label: 'Severity', h24: 'O', h72: 'O', fin: 'X', src: 'auto', val: c.cvss !== null ? `${c.cvss} (${c.severity ?? 'n/a'})` : undefined, mono: true },
  { n: 'v24', label: 'Impact', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'affected functionality / data exposure' },
  { n: 'v25', label: 'Malicious actor exploiting it', h24: 'O', h72: 'O', fin: 'I', src: 'you', hint: 'if attribution exists' },
  { n: 'v26', label: 'Security-update details', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'hashes, URLs, signing keys of the fix' },
];

const incident = (c: Ctx): Q16Field[] => [
  { n: 'i13', label: 'Suspected unlawful or malicious acts', h24: 'X', h72: 'C', fin: 'C', src: 'you', hint: 'true/false — Art.14(4)(a): the one extra thing the 24h warning must state' },
  { n: 'i14', label: 'General information about the incident', h24: 'O', h72: 'X', fin: 'C', src: 'auto', val: 'from your general information' },
  { n: 'i15', label: 'Date/time the incident was detected', h24: 'O', h72: 'X', fin: 'C', src: 'auto', val: '2026-07-18 08:12 UTC', mono: true },
  { n: 'i16', label: 'Date/time the incident occurred', h24: 'O', h72: 'X', fin: 'C', src: 'you', hint: 'may precede detection' },
  { n: 'i17', label: 'Initial assessment of the incident', h24: 'O', h72: 'X', fin: 'C', src: 'you', hint: 'first severity/scope read' },
  { n: 'i18', label: 'Corrective measures taken', h24: 'O', h72: 'X', fin: 'C', src: 'you', hint: 'containment / patch / mitigation' },
  { n: 'i19', label: 'Measures users can take', h24: 'O', h72: 'X', fin: 'C', src: 'you', hint: 'workarounds, config, monitoring' },
  { n: 'i20', label: 'Considered sensitivity of information', h24: 'O', h72: 'I', fin: 'C', src: 'you', val: 'false' },
  { n: 'i21', label: 'Detailed description of the incident', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'mandatory at Final' },
  { n: 'i22', label: 'Severity of the incident', h24: 'O', h72: 'O', fin: 'X', src: 'auto', val: c.cvss !== null ? `${c.cvss} (${c.severity ?? 'n/a'})` : undefined, mono: true },
  { n: 'i23', label: 'Impact of the incident', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'affected functionality / users / data' },
  { n: 'i24', label: 'Type of threat / root cause', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'mandatory at Final' },
  { n: 'i25', label: 'Applied and ongoing mitigations', h24: 'O', h72: 'O', fin: 'X', src: 'you', hint: 'mandatory at Final' },
];

export function q16Fields(c: Ctx): Q16Field[] {
  return [...common(c), ...(c.kind === 'incident' ? incident(c) : vuln(c))];
}
